import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import * as icons from "lucide-react";
import { ArrowDown, ArrowRight, ArrowUpRight } from "lucide-react";
import Marquee from "@/components/Marquee";
import MediaSlot from "@/components/MediaSlot";
import { MaskedLine, Reveal, SectionTag } from "@/components/Reveal";
import { HERO, PROFILE, STICKERS, MANIFESTO, IDENTITIES, UNIVERSE, CHAPTERS, STATS, CONTACT } from "@/data";

const STICKER_COLORS = {
  ink: "bg-ink text-paper",
  ember: "bg-ember text-ink",
  burgundy: "bg-burgundy text-paper",
  paper: "bg-paper text-ink border border-ink/20",
};

const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const px = useTransform(sx, [0, 1], [-18, 18]);
  const py = useTransform(sy, [0, 1], [-14, 14]);
  const rx = useTransform(sy, [0, 1], [3, -3]);
  const ry = useTransform(sx, [0, 1], [-4, 4]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      className="relative flex min-h-screen flex-col justify-center overflow-hidden px-6 pt-28 md:px-12"
      data-testid="hero-section"
    >
      <div className="pointer-events-none absolute left-1/2 top-24 -translate-x-1/2 select-none font-display text-[26vw] font-black uppercase leading-none tracking-tighter text-ink/[0.04] md:top-12">
        KIN
      </div>

      <div className="relative z-10 grid items-center gap-10 md:grid-cols-12">
        <div className="relative z-20 md:col-span-7">
          <MaskedLine delay={0.15}>
            <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-burgundy md:text-xs md:tracking-[0.4em]">
              {HERO.intro}
            </span>
          </MaskedLine>
          <h1 className="mt-6 font-display text-[2.5rem] font-black uppercase leading-[0.9] tracking-tighter sm:text-7xl md:text-[6.5rem]" data-testid="hero-headline">
            {HERO.lines.map((l, i) => (
              <MaskedLine key={l} delay={0.3 + i * 0.15}>
                <span className={i === 1 ? "text-burgundy" : ""}>{l}</span>
              </MaskedLine>
            ))}
          </h1>
          <MaskedLine delay={0.85}>
            <span className="mt-4 block font-serif text-2xl italic text-ash md:text-3xl">
              {HERO.handwritten}
            </span>
          </MaskedLine>
          <Reveal delay={1.05}>
            <p className="mt-8 max-w-md text-sm leading-relaxed text-ash md:text-base" data-testid="hero-copy">
              {HERO.copy}
            </p>
            <button
              data-testid="enter-world-button"
              data-cursor="ENTER"
              onClick={() => window.__lenis?.scrollTo("#manifesto", { offset: -40 })}
              className="group mt-8 inline-flex items-center gap-3 border border-ink px-7 py-4 font-display text-xs font-bold uppercase tracking-[0.3em] transition-colors duration-300 hover:bg-ink hover:text-paper"
            >
              Enter my world
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
          </Reveal>
        </div>

        <div className="relative md:col-span-5">
          <motion.div style={{ x: px, y: py, rotateX: rx, rotateY: ry, transformPerspective: 900 }}>
            <MediaSlot
              src={PROFILE.heroImage}
              slot="HERO_PROFILE_IMAGE"
              ratio="aspect-[3/4]"
              className="border border-ink/15 shadow-[16px_16px_0_0_#7A1C21]"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.3 }}
            className="absolute -right-4 -top-8 h-24 w-24 animate-spin-slow md:-right-6"
            aria-hidden
          >
            <svg viewBox="0 0 100 100" className="h-full w-full">
              <defs>
                <path id="circlePath" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
              </defs>
              <circle cx="50" cy="50" r="49" fill="#FF4500" />
              <text className="fill-ink font-display text-[10.5px] font-bold uppercase" letterSpacing="2.5">
                <textPath href="#circlePath">Kin Hakan · 2026 · Record of Becoming ·</textPath>
              </text>
            </svg>
          </motion.div>
        </div>
      </div>

      {STICKERS.map((s, i) => {
        const Icon = icons[s.icon];
        return (
          <motion.span
            key={s.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 + i * 0.12 }}
            style={{ left: s.x, top: s.y, "--rot": s.rot }}
            className={`animate-floaty absolute z-10 hidden items-center gap-2 px-4 py-2 font-display text-[9px] font-bold uppercase tracking-[0.2em] lg:flex ${STICKER_COLORS[s.color]}`}
          >
            {Icon && <Icon className="h-3.5 w-3.5" />}
            {s.label}
          </motion.span>
        );
      })}
    </section>
  );
};

const Manifesto = () => (
  <section id="manifesto" className="px-6 py-28 md:px-12 md:py-40" data-testid="manifesto-section">
    <SectionTag no="01" label={MANIFESTO.label} />
    <div className="mt-12 max-w-4xl">
      {MANIFESTO.lines.map((l, i) => (
        <MaskedLine key={l} delay={i * 0.08}>
          <span className="font-display text-3xl font-bold leading-snug tracking-tight sm:text-4xl md:text-5xl">
            {l}
          </span>
        </MaskedLine>
      ))}
      <Reveal delay={0.3}>
        <p className="mt-10 max-w-2xl font-serif text-xl italic leading-relaxed text-burgundy md:text-2xl">
          {MANIFESTO.closing}
        </p>
      </Reveal>
    </div>
  </section>
);

const WhatIDo = () => (
  <section className="border-t border-ink/10 px-6 py-24 md:px-12" data-testid="what-i-do-section">
    <SectionTag no="02" label="WHAT I DO" />
    <div className="mt-10">
      {IDENTITIES.map((it, i) => (
        <Reveal key={it.key} delay={i * 0.05}>
          <Link
            to={it.to}
            data-testid={`identity-${it.key.toLowerCase()}`}
            data-cursor="EXPLORE"
            className="group grid items-center gap-2 border-b border-ink/10 py-8 transition-colors duration-300 hover:bg-cream md:grid-cols-12 md:gap-6 md:py-10"
          >
            <span className="font-display text-xs text-burgundy md:col-span-1">{it.no}</span>
            <h3 className="font-display text-4xl font-black uppercase tracking-tight transition-transform duration-300 group-hover:translate-x-3 md:col-span-5 md:text-6xl">
              {it.title}
            </h3>
            <p className="text-sm text-ash md:col-span-5 md:text-base">{it.desc}</p>
            <ArrowUpRight className="hidden h-8 w-8 text-burgundy opacity-0 transition-opacity group-hover:opacity-100 md:col-span-1 md:block" />
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

const Universe = () => (
  <section className="bg-ink px-6 py-28 text-paper md:px-12 md:py-36" data-testid="kin-universe-section">
    <SectionTag no="03" label="THE KIN UNIVERSE — PILIH JALURMU" dark />
    <div className="mt-14 grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-3 md:gap-y-16">
      {UNIVERSE.map((u, i) => (
        <Reveal key={u.word} delay={i * 0.06} className={i % 3 === 1 ? "md:translate-y-8" : i % 3 === 2 ? "md:-translate-y-4" : ""}>
          <Link to={u.to} data-testid={`universe-${u.word.toLowerCase()}`} data-cursor="GO" className="group block">
            <span className="font-display text-[10px] uppercase tracking-[0.3em] text-ember">0{i + 1}</span>
            <span className="block font-display text-4xl font-black uppercase tracking-tighter transition-colors duration-300 group-hover:text-ember sm:text-5xl md:text-6xl">
              {u.word}
            </span>
            <span className="mt-2 block font-serif text-base italic text-paper/50">{u.note}</span>
            <span className="mt-3 block h-px w-0 bg-ember transition-[width] duration-500 group-hover:w-full" />
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

const Timeline = () => (
  <section className="px-6 py-24 md:px-12" data-testid="timeline-section">
    <div className="flex items-end justify-between">
      <SectionTag no="04" label="A RECORD OF BECOMING" />
      <Link to="/about" className="u-link hidden font-display text-xs font-bold uppercase tracking-[0.25em] text-burgundy md:block" data-testid="timeline-about-link">
        Full story →
      </Link>
    </div>
    <div className="no-scrollbar mt-12 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-6" data-testid="chapter-timeline">
      {CHAPTERS.map((c) => (
        <Link
          to="/about"
          key={c.no}
          data-testid={`chapter-${c.no}`}
          data-cursor="OPEN"
          className="group w-72 shrink-0 snap-start border border-ink/15 p-7 transition-colors duration-300 hover:border-burgundy hover:bg-ink hover:text-paper md:w-80"
        >
          <span className="font-display text-5xl font-black text-burgundy transition-colors group-hover:text-ember">{c.no}</span>
          <h4 className="mt-6 font-display text-xl font-black uppercase tracking-tight">{c.title}</h4>
          <p className="mt-3 text-sm leading-relaxed text-ash transition-colors group-hover:text-paper/70">{c.text}</p>
        </Link>
      ))}
    </div>
  </section>
);

const Numbers = () => (
  <section className="border-y border-ink/10 bg-cream px-6 py-24 md:px-12" data-testid="by-the-numbers-section">
    <SectionTag no="05" label="BY THE NUMBERS" />
    <div className="mt-12 grid grid-cols-2 gap-px bg-ink/10 md:grid-cols-3">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.05} className="bg-cream">
          <div className="p-6 md:p-10" data-testid={`stat-${i}`}>
            <span className="font-display text-5xl font-black tracking-tighter text-burgundy md:text-7xl">{s.value}</span>
            <p className="mt-3 font-display text-xs font-bold uppercase tracking-[0.2em] md:text-sm">{s.label}</p>
            <p className="mt-1 text-xs text-ash">{s.sub}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const FinalCTA = () => (
  <section className="relative overflow-hidden bg-ink px-6 py-32 text-paper md:px-12 md:py-44" data-testid="home-final-cta">
    <Marquee items={["WHAT CAN WE CREATE NEXT?", "APA YANG BISA KITA BUAT BARENG?"]} dark fast className="absolute inset-x-0 top-0" />
    <div className="mt-16 text-center">
      <MaskedLine>
        <span className="font-display text-5xl font-black uppercase leading-[0.95] tracking-tighter sm:text-6xl md:text-8xl">
          {CONTACT.finalQuestion}
        </span>
      </MaskedLine>
      <Reveal delay={0.2}>
        <p className="mx-auto mt-8 max-w-xl text-sm leading-relaxed text-paper/60 md:text-base">{CONTACT.finalSub}</p>
        <Link
          to="/contact"
          data-testid="home-cta-collaborate"
          data-cursor="GO"
          className="group mt-10 inline-flex items-center gap-3 bg-ember px-10 py-5 font-display text-sm font-black uppercase tracking-[0.3em] text-ink transition-transform duration-300 hover:-translate-y-1"
        >
          Let's talk
          <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const Home = () => (
  <motion.main
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -24 }}
    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    data-testid="home-page"
  >
    <Hero />
    <Marquee items={HERO.marquee} />
    <Manifesto />
    <WhatIDo />
    <Universe />
    <Timeline />
    <Numbers />
    <FinalCTA />
  </motion.main>
);

export default Home;
